/**
 * Responsibility: pick notify lists, reusing ones that already exist wherever
 * possible.
 *
 * A notify list routes alerts to notification channels, and matching is exact.
 * That makes a near-miss the expensive failure: "costco" and "costcos" look
 * alike on the page and route to different places, so a typo produces a watch
 * that quietly alerts nowhere.
 *
 * So existing lists are offered first and ranked by how likely they are to be
 * what is being typed - a prefix match beats a match in the middle of a word -
 * and creating a new one stays possible but takes a deliberate extra step.
 */
import { Autocomplete, Chip, TextField, createFilterOptions } from '@mui/material';

type NotifyListPickerProps = {
  value: string[];
  onChange: (names: string[]) => void;
  options: string[];
  label?: string;
  helperText?: string;
  autoFocus?: boolean;
};

const CREATE_PREFIX = 'Create ';

const filter = createFilterOptions<string>({
  trim: true,
  ignoreCase: true,
  stringify: (option) => option,
});

/** Normalised the same way the server normalises, so what you see is stored. */
function normalise(name: string): string {
  return name.trim().toLowerCase();
}

export function NotifyListPicker({
  value,
  onChange,
  options,
  label = 'Notify lists',
  helperText = 'Alerts for a list go to every channel that sends it.',
  autoFocus = false,
}: NotifyListPickerProps) {
  return (
    <Autocomplete
      multiple
      freeSolo
      autoHighlight
      options={options}
      value={value}
      onChange={(_event, next) => {
        const cleaned = next
          .map((entry) =>
            entry.startsWith(CREATE_PREFIX) ? entry.slice(CREATE_PREFIX.length).replace(/"/g, '') : entry,
          )
          .map(normalise)
          .filter(Boolean);
        onChange(Array.from(new Set(cleaned)));
      }}
      filterOptions={(available, params) => {
        const matched = filter(available, params);
        const typed = normalise(params.inputValue);

        // Rank a prefix match above a match buried mid-word: typing "cos"
        // should offer "costco" before "my-costco-list".
        matched.sort((a, b) => {
          const aStarts = a.startsWith(typed) ? 0 : 1;
          const bStarts = b.startsWith(typed) ? 0 : 1;
          return aStarts - bStarts || a.localeCompare(b);
        });

        // Offering to create one that already exists would produce a
        // duplicate-looking entry that normalises to the same list.
        if (typed && !available.some((option) => normalise(option) === typed)) {
          matched.push(`${CREATE_PREFIX}"${typed}"`);
        }
        return matched;
      }}
      renderTags={(names, getTagProps) =>
        names.map((name, index) => {
          const { key, ...chipProps } = getTagProps({ index });
          return <Chip key={key} size="small" variant="outlined" label={name} {...chipProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={helperText}
          autoFocus={autoFocus}
          placeholder="costco, etb"
        />
      )}
    />
  );
}
